/** High-level API service functions grouped by domain. */

import { api, ApiError } from "./api";
import type {
  Annotation,
  AnnotationInput,
  AnnotationReply,
  CompileResult,
  CompileStatus,
  FileContent,
  FileNode,
  GitDiff,
  GitStatus,
  LatexCommandsReport,
  SyncTexResult,
  User,
} from "../types";

export const AuthService = {
  login: (username: string, password: string) =>
    api.post<User>("/api/auth/login", { username, password }),
  logout: () => api.post<{ ok: boolean }>("/api/auth/logout"),
  me: () => api.get<User>("/api/auth/me"),
};

export const FilesService = {
  tree: () => api.get<FileNode>("/api/files/tree"),
  content: (path: string) =>
    api.get<FileContent>(`/api/files/content?path=${encodeURIComponent(path)}`),
  write: (path: string, content: string) =>
    api.put<{ path: string; status: string }>("/api/files/content", { path, content }),
  create: (path: string) =>
    api.post<{ path: string; status: string }>("/api/files", { path }),
  createDirectory: (path: string) =>
    api.post<{ path: string; status: string }>("/api/files/directory", { path }),
  rename: (path: string, newPath: string) =>
    api.patch<{ path: string; status: string }>("/api/files/rename", { path, new_path: newPath }),
  remove: (path: string) =>
    api.delete<{ ok: boolean }>(`/api/files?path=${encodeURIComponent(path)}`),
  latexCommands: () => api.get<LatexCommandsReport>("/api/files/latex-commands"),
};

export const CompileService = {
  compile: (path: string) =>
    api.post<CompileResult>("/api/compile", { path }),
  status: (path: string) =>
    api.get<CompileStatus>(`/api/compile/status?path=${encodeURIComponent(path)}`),
  pdfUrl: (path: string) =>
    `/api/compile/pdf?path=${encodeURIComponent(path)}&t=${Date.now()}`,
  synctexForward: (path: string, file: string, line: number, column: number = 0) =>
    api.post<SyncTexResult>("/api/compile/synctex-forward", { path, file, line, column }),
};

export const GitService = {
  status: () => api.get<GitStatus>("/api/git/status"),
  diff: (staged = false) =>
    api.get<GitDiff>(`/api/git/diff${staged ? "?staged=true" : ""}`),
  diffFile: (path: string) =>
    api.get<{ diff: string }>(`/api/git/diff-file?path=${encodeURIComponent(path)}`),
  fileOriginal: (path: string) =>
    api.get<{ content: string }>(`/api/git/file-original?path=${encodeURIComponent(path)}`),
  commit: (message: string, push: boolean) =>
    api.post<{ summary: string }>("/api/git/commit", { message, push }),
  push: () => api.post<{ summary: string }>("/api/git/push"),
  reset: () => api.post<{ summary: string }>("/api/git/reset"),
};

export const AnnotationsService = {
  list: (document?: string, includeDeleted = false, allDocuments = false) => {
    const params = new URLSearchParams();
    if (document) params.set("document", document);
    if (includeDeleted) params.set("include_deleted", "true");
    if (allDocuments) params.set("all_documents", "true");
    const query = params.toString();
    return api.get<Annotation[]>(`/api/annotations${query ? `?${query}` : ""}`);
  },
  create: (input: AnnotationInput) =>
    api.post<Annotation>("/api/annotations", input),
  update: (id: number, body: Partial<AnnotationInput>) =>
    api.patch<Annotation>(`/api/annotations/${id}`, body),
  resolve: (id: number, resolved = true) =>
    api.post<Annotation>(`/api/annotations/${id}/resolve`, { resolved }),
  reply: (id: number, content: string) =>
    api.post<AnnotationReply>(`/api/annotations/${id}/reply`, { content }),
  remove: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/annotations/${id}`),
  restore: (id: number) =>
    api.post<Annotation>(`/api/annotations/${id}/restore`),
};

export { ApiError };
